import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/header';

const API_BASE = '/api';

const DashboardPage = () => {
  console.log('DashboardPage component initializing');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [storeInfo, setStoreInfo] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sortBy, setSortBy] = useState('name'); // 'name', 'price', 'category', 'date'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  
  // Store settings state
  const [activeTab, setActiveTab] = useState('products');
  const [storeForm, setStoreForm] = useState({
    name: '',
    description: '',
    city: '',
    has_physical_location: false,
    address: '',
    website: '',
    contact_email: '',
    phone_number: '',
    business_hours: {},
    social_media: {},
    business_type: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // Helper function to get the best image URL from a product
  const getProductImageUrl = (product) => {
    // New multiple images system
    if (product.images && product.images.length > 0) {
      // Find primary image first
      const primaryImage = product.images.find(img => img.is_primary);
      if (primaryImage) {
        return primaryImage.image_url;
      }
      
      // If no primary image, use first image
      return product.images[0].image_url;
    }
    
    // Fallback to legacy single image_url
    if (product.image_url) {
      return product.image_url;
    }
    
    // No image available
    return null;
  };

  // Helper function to process image URLs
  const processImageUrl = (imageUrl) => {
    if (!imageUrl) return '/placeholder-image.png';
    
    if (imageUrl.startsWith('/uploads')) {
      return `https://storetoyou.ca${imageUrl}`;
    } else if (imageUrl.startsWith('/api/')) {
      return `https://storetoyou.ca${imageUrl}`;
    } else if (imageUrl.startsWith('http')) {
      return imageUrl;
    } else {
      return `https://storetoyou.ca/api${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
    }
  };

  // Check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.exp) return false;
      return payload.exp * 1000 < Date.now();
    } catch (e) {
      console.error('Error checking token expiration:', e);
      return true; 
    }
  };

  // Handle unauthorized responses
  const handleUnauthorized = () => {
    console.log('Token expired or unauthorized, redirecting to login');
    localStorage.removeItem('token');
    navigate('/login');
  };

  // Filter and sort products
  const getFilteredAndSortedProducts = () => {
    let filtered = [...products];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Apply category filter
    if (selectedCategory) {
      filtered = filtered.filter(product => 
        product.category_name === selectedCategory
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'price':
          aValue = parseFloat(a.price) || 0;
          bValue = parseFloat(b.price) || 0;
          break;
        case 'category':
          aValue = a.category_name || 'zzz';
          bValue = b.category_name || 'zzz';
          break;
        case 'date':
          aValue = new Date(a.created_at || 0);
          bValue = new Date(b.created_at || 0);
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
    
    return filtered;
  };

  // Get current products for pagination
  const getCurrentProducts = () => {
    const filteredProducts = getFilteredAndSortedProducts();
    const indexOfLastProduct = currentPage * productsPerPage;
    const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
    return filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  };

  // Calculate total pages
  const getTotalPages = () => {
    const filteredProducts = getFilteredAndSortedProducts();
    return Math.ceil(filteredProducts.length / productsPerPage);
  };

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top of products section
    document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle sort change
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Reset filters
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSortBy('name');
    setSortOrder('asc');
    setCurrentPage(1);
  };

  // Fetch store information and products
  useEffect(() => {
    console.log('useEffect running');
    const token = localStorage.getItem('token');
    console.log('Token retrieved from localStorage:', token ? 'Token exists' : 'No token found');

    if (!token || isTokenExpired(token)) {
      console.log('No token or expired token, redirecting to login');
      handleUnauthorized();
      return;
    }

    // Debug token structure (optional)
    if (token) {
      try {
        const [header, payload, signature] = token.split('.');
        console.log('Token parts:', {
          header: atob(header),
          payload: atob(payload),
          signatureExists: !!signature
        });
      } catch (e) {
        console.error('Token parsing error:', e);
      }
    }

    const fetchData = async () => {
      try {
        // 1) Fetch store information
        const storeResponse = await fetch(`${API_BASE}/user/store`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Handle unauthorized or forbidden responses
        if (storeResponse.status === 401 || storeResponse.status === 403) {
          handleUnauthorized();
          return;
        }

        if (storeResponse.status === 404) {
          console.log('No store found for user, redirecting to create store page');
          navigate('/createstore');
          return;
        }

        if (!storeResponse.ok) {
          throw new Error('Failed to fetch store information');
        }

        const storeData = await storeResponse.json();
        setStoreInfo(storeData);

        // Initialize the store form with fetched data
        setStoreForm({
          name: storeData.name || '',
          description: storeData.description || '',
          city: storeData.city || '',
          has_physical_location: storeData.has_physical_location || false,
          address: storeData.address || '',
          website: storeData.website || '',
          contact_email: storeData.contact_email || '',
          phone_number: storeData.phone_number || '',
          business_hours: storeData.business_hours
            ? typeof storeData.business_hours === 'string'
              ? JSON.parse(storeData.business_hours)
              : storeData.business_hours
            : {},
          social_media: storeData.social_media
            ? typeof storeData.social_media === 'string'
              ? JSON.parse(storeData.social_media)
              : storeData.social_media
            : {},
          business_type: storeData.business_type || ''
        });

        // Set logo preview if store has a logo
        if (storeData.profile_image_url) {
          // Build the absolute URL for the preview using HTTPS
          setLogoPreview(`https://storetoyou.ca${storeData.profile_image_url}`);
        }

        // 2) Fetch products
        const productsResponse = await fetch(
          `${API_BASE}/stores/${storeData.url_name}/products`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (productsResponse.status === 401 || productsResponse.status === 403) {
          handleUnauthorized();
          return;
        }

        if (!productsResponse.ok) {
          throw new Error('Failed to fetch products');
        }

        const productsData = await productsResponse.json();
        console.log('Products data received:', productsData);
        setProducts(Array.isArray(productsData) ? productsData : []);

        // 3) Fetch categories
        const categoriesResponse = await fetch(`${API_BASE}/categories`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (categoriesResponse.status === 401 || categoriesResponse.status === 403) {
          handleUnauthorized();
          return;
        }

        if (categoriesResponse.ok) {
          const categoriesData = await categoriesResponse.json();
          setCategories(categoriesData);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);

        if (err.message && err.message.includes('unauthorized')) {
          handleUnauthorized();
          return;
        }
        
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  // Format price helper function
  const formatPrice = (price) => {
    if (typeof price === 'string') {
      price = parseFloat(price);
    }
    return typeof price === 'number' && !isNaN(price) ? price.toFixed(2) : '0.00';
  };

  // Handle logo file selection
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (JPEG, PNG, etc.)');
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      setTimeout(() => setError(null), 3000);
      return;
    }

    setLogoFile(file);
    const previewUrl = URL.createObjectURL(file);
    setLogoPreview(previewUrl);
  };

  // Handle logo upload
  const handleLogoUpload = async () => {
    if (!logoFile || !storeInfo) return;

    const token = localStorage.getItem('token');
    if (!token || isTokenExpired(token)) {
      handleUnauthorized();
      return;
    }

    setUploadingLogo(true);

    try {
      const formData = new FormData();
      formData.append('logo', logoFile);

      const response = await fetch(
        `${API_BASE}/stores/${storeInfo.id}/logo`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        }
      );

      if (response.status === 401 || response.status === 403) {
        handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to upload logo');
      }

      const data = await response.json();

      // Update store info with new logo URL
      setStoreInfo({
        ...storeInfo,
        profile_image_url: data.url
      });

      setSuccessMessage('Logo uploaded successfully');
      setTimeout(() => setSuccessMessage(''), 3000);

      // Clear the file input
      setLogoFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError('Failed to upload logo: ' + err.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setUploadingLogo(false);
    }
  };

  // Handle logo removal
  const handleRemoveLogo = () => {
    setLogoFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    // If no new logo was selected, keep the current store logo
    if (storeInfo?.profile_image_url && !logoFile) {
      setLogoPreview(`https://storetoyou.ca${storeInfo.profile_image_url}`);
    } else {
      setLogoPreview(null);
    }
  };

  // Handle product deletion
  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    const token = localStorage.getItem('token');
    if (!token || isTokenExpired(token)) {
      handleUnauthorized();
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/stores/${storeInfo.url_name}/products/${productId}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.status === 401 || response.status === 403) {
        handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to delete product');
      }

      setProducts(products.filter((p) => p.id !== productId));
      setSuccessMessage('Product deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Check if current page is now empty after deletion
      const filteredProducts = getFilteredAndSortedProducts().filter(p => p.id !== productId);
      const newTotalPages = Math.ceil(filteredProducts.length / productsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
    } catch (err) {
      console.error('Error deleting product:', err);
      if (err.message && err.message.includes('unauthorized')) {
        handleUnauthorized();
        return;
      }
      setError('Failed to delete product');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Store settings form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setStoreForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setStoreForm((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSocialMediaChange = (platform, value) => {
    setStoreForm((prev) => ({
      ...prev,
      social_media: {
        ...prev.social_media,
        [platform]: value
      }
    }));
  };

  const handleBusinessHoursChange = (day, field, value) => {
    setStoreForm((prev) => {
      const updatedHours = { ...prev.business_hours };
      if (!updatedHours[day]) {
        updatedHours[day] = { open: '09:00', close: '17:00', isClosed: false };
      }
      updatedHours[day][field] = value;
      return { ...prev, business_hours: updatedHours };
    });
  };

  // Save store settings
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token || isTokenExpired(token)) {
        handleUnauthorized();
        return;
      }

      const updateData = {
        name: storeForm.name,
        description: storeForm.description,
        city: storeForm.city,
        has_physical_location: storeForm.has_physical_location,
        address: storeForm.address,
        website: storeForm.website,
        contact_email: storeForm.contact_email,
        phone_number: storeForm.phone_number,
        business_hours: storeForm.business_hours,
        social_media: storeForm.social_media,
        business_type: storeForm.business_type
      };

      const response = await fetch(`${API_BASE}/stores/${storeInfo.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      if (response.status === 401 || response.status === 403) {
        handleUnauthorized();
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to update store information');
      }

      const updatedStore = await response.json();
      setStoreInfo(updatedStore);
      setSuccessMessage('Store information updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      setActiveTab('products');
    } catch (err) {
      console.error('Error updating store:', err);
      setError('Failed to update store: ' + err.message);
    } finally {
      setSavingSettings(false);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <>
        <Header />
        <div className="container mt-5">
          <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Render error state
  if (error) {
    return (
      <>
        <Header />
        <div className="container mt-5">
          <div className="alert alert-danger" role="alert">
            Error: {error}
          </div>
        </div>
      </>
    );
  }

  // Pagination component
  const Pagination = () => {
    const totalPages = getTotalPages();
    const filteredProductsCount = getFilteredAndSortedProducts().length;
    
    if (totalPages <= 1) return null;
    
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          <small className="text-muted">
            Showing {Math.min((currentPage - 1) * productsPerPage + 1, filteredProductsCount)} to{' '}
            {Math.min(currentPage * productsPerPage, filteredProductsCount)} of {filteredProductsCount} products
          </small>
        </div>
        
        <nav aria-label="Product pagination">
          <ul className="pagination mb-0">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              >
                First
              </button>
            </li>
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
            </li>
            
            {startPage > 1 && (
              <>
                <li className="page-item disabled">
                  <span className="page-link">...</span>
                </li>
              </>
            )}
            
            {pageNumbers.map((number) => (
              <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => handlePageChange(number)}
                >
                  {number}
                </button>
              </li>
            ))}
            
            {endPage < totalPages && (
              <>
                <li className="page-item disabled">
                  <span className="page-link">...</span>
                </li>
              </>
            )}
            
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </li>
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </button>
            </li>
          </ul>
        </nav>
        
        <div>
          <select
            className="form-select form-select-sm"
            value={productsPerPage}
            onChange={(e) => {
              setProductsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </select>
        </div>
      </div>
    );
  };

  return (
    <>
      <Header />
      <div className="container-fluid bg-light py-5">
        <div className="container">
          <div className="card shadow">
            <div className="card-body">
              {/* Store Information */}
              {storeInfo && (
                <div className="mb-4">
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      <h2>
                        <span
                          onClick={() =>
                            navigate(`/${storeInfo.city.toLowerCase()}/${storeInfo.url_name}`)
                          }
                          style={{ cursor: 'pointer', color: '#007bff' }}
                          className="text-decoration-none hover-underline"
                          role="link"
                        >
                          {storeInfo.name}
                        </span>
                      </h2>
                      <p className="text-muted">{storeInfo.city}</p>
                    </div>

                    {/* Only show logo section in products tab */}
                    {activeTab === 'products' && (
                      <div className="col-md-4">
                        <div className="card">
                          <div className="card-header">
                            <h5 className="card-title mb-0">Store Logo</h5>
                          </div>
                          <div className="card-body text-center">
                            {logoPreview ? (
                              <div className="mb-3">
                                <img
                                  src={logoPreview}
                                  alt="Store Logo Preview"
                                  className="img-thumbnail"
                                  style={{
                                    maxWidth: '150px',
                                    maxHeight: '150px',
                                    objectFit: 'contain'
                                  }}
                                />
                              </div>
                            ) : (
                              <div
                                className="bg-light d-flex align-items-center justify-content-center mb-3"
                                style={{
                                  width: '150px',
                                  height: '150px',
                                  margin: '0 auto',
                                  border: '1px dashed #ccc'
                                }}
                              >
                                <span className="text-muted">No logo</span>
                              </div>
                            )}

                            <div className="mb-3">
                              <input
                                type="file"
                                className="form-control"
                                accept="image/*"
                                onChange={handleLogoChange}
                                ref={fileInputRef}
                                id="logoInput"
                              />
                            </div>

                            <div className="d-flex justify-content-center gap-2">
                              <button
                                className="btn btn-primary"
                                onClick={handleLogoUpload}
                                disabled={!logoFile || uploadingLogo}
                              >
                                {uploadingLogo ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                    Uploading...
                                  </>
                                ) : (
                                  'Upload Logo'
                                )}
                              </button>
                              {logoFile && (
                                <button
                                  className="btn btn-outline-secondary"
                                  onClick={handleRemoveLogo}
                                  disabled={uploadingLogo}
                                >
                                  Cancel
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Success Message */}
              {successMessage && (
                <div
                  className="alert alert-success alert-dismissible fade show"
                  role="alert"
                >
                  {successMessage}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSuccessMessage('')}
                  ></button>
                </div>
              )}

              {/* Dashboard Tabs */}
              <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'products' ? 'active' : ''}`}
                    onClick={() => setActiveTab('products')}
                  >
                    <i className="bi bi-grid me-1"></i> Products ({products.length})
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`}
                    onClick={() => setActiveTab('settings')}
                  >
                    <i className="bi bi-gear me-1"></i> Store Settings
                  </button>
                </li>
              </ul>

              {/* Product Tab Content */}
              {activeTab === 'products' && (
                <div id="products-section">
                  {/* Action Button and Stats */}
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                      <h3 className="mb-0">Products</h3>
                      <small className="text-muted">
                        {getFilteredAndSortedProducts().length} products found
                      </small>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate('/dashboard/add')}
                    >
                      <i className="fas fa-plus me-2"></i>Add New Product
                    </button>
                  </div>

                  {/* Filters and Search */}
                  <div className="card mb-4">
                    <div className="card-body">
                      <div className="row g-3">
                        <div className="col-md-4">
                          <label htmlFor="searchInput" className="form-label">Search Products</label>
                          <input
                            type="text"
                            className="form-control"
                            id="searchInput"
                            placeholder="Search by name or description..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                        
                        <div className="col-md-3">
                          <label htmlFor="categoryFilter" className="form-label">Category</label>
                          <select
                            className="form-select"
                            id="categoryFilter"
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                          >
                            <option value="">All Categories</option>
                            {[...new Set(products.map(p => p.category_name).filter(Boolean))].sort().map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="col-md-3">
                          <label htmlFor="sortBy" className="form-label">Sort By</label>
                          <div className="input-group">
                            <select
                              className="form-select"
                              id="sortBy"
                              value={sortBy}
                              onChange={(e) => {
                                setSortBy(e.target.value);
                                setCurrentPage(1);
                              }}
                            >
                              <option value="name">Name</option>
                              <option value="price">Price</option>
                              <option value="category">Category</option>
                              <option value="date">Date Added</option>
                            </select>
                            <button
                              className="btn btn-outline-secondary"
                              type="button"
                              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                              title={sortOrder === 'asc' ? 'Sort Descending' : 'Sort Ascending'}
                            >
                              <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                            </button>
                          </div>
                        </div>
                        
                        <div className="col-md-2 d-flex align-items-end">
                          <button
                            className="btn btn-outline-secondary w-100"
                            onClick={resetFilters}
                          >
                            <i className="fas fa-redo me-2"></i>Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Products Table */}
                  {products.length === 0 ? (
                    <div className="alert alert-info">
                      No products found. Add your first product to get started!
                    </div>
                  ) : getFilteredAndSortedProducts().length === 0 ? (
                    <div className="alert alert-warning">
                      No products match your current filters. Try adjusting your search criteria.
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead className="table-light">
                            <tr>
                              <th>Image</th>
                              <th 
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleSort('name')}
                              >
                                Name {sortBy === 'name' && (
                                  <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                                )}
                              </th>
                              <th 
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleSort('price')}
                              >
                                Price {sortBy === 'price' && (
                                  <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                                )}
                              </th>
                              <th 
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleSort('category')}
                              >
                                Category {sortBy === 'category' && (
                                  <i className={`fas fa-sort-${sortOrder === 'asc' ? 'up' : 'down'} ms-1`}></i>
                                )}
                              </th>
                              <th>Images</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {getCurrentProducts().map((product) => {
                              const imageUrl = getProductImageUrl(product);
                              const processedImageUrl = processImageUrl(imageUrl);
                              const imageCount = product.images ? product.images.length : 0;
                              const hasMultipleImages = imageCount > 1;
                              
                              return (
                                <tr key={product.id}>
                                  <td style={{ width: '100px' }}>
                                    <div
                                      onClick={() =>
                                        navigate(
                                          `/${storeInfo.city.toLowerCase()}/${storeInfo.url_name}/product/${product.id}`
                                        )
                                      }
                                      style={{ cursor: 'pointer', position: 'relative' }}
                                      role="button"
                                      className="d-inline-block"
                                    >
                                      {imageUrl ? (
                                        <>
                                          <img
                                            src={processedImageUrl}
                                            alt={product.name}
                                            className="img-thumbnail"
                                            style={{
                                              width: '50px',
                                              height: '50px',
                                              objectFit: 'cover'
                                            }}
                                            onError={(e) => {
                                              e.target.onerror = null;
                                              e.target.src = '/placeholder-image.png';
                                            }}
                                          />
                                          {hasMultipleImages && (
                                            <span 
                                              className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-primary"
                                              style={{ fontSize: '0.6rem' }}
                                            >
                                              <i className="fas fa-images"></i>
                                            </span>
                                          )}
                                        </>
                                      ) : (
                                        <div
                                          className="bg-secondary d-flex align-items-center justify-content-center"
                                          style={{ width: '50px', height: '50px' }}
                                        >
                                          <i className="fas fa-image text-white"></i>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                  <td className="align-middle">{product.name}</td>
                                  <td className="align-middle">
                                    ${formatPrice(product.price)}
                                  </td>
                                  <td className="align-middle">
                                    {product.category_name || 'Uncategorized'}
                                  </td>
                                  <td className="align-middle">
                                    <div className="d-flex align-items-center">
                                      <span className={`badge ${imageCount > 0 ? 'bg-success' : 'bg-secondary'} me-2`}>
                                        {imageCount}
                                      </span>
                                      {imageCount > 1 && (
                                        <small className="text-muted">
                                          <i className="fas fa-images me-1"></i>
                                          Gallery
                                        </small>
                                      )}
                                      {imageCount === 1 && (
                                        <small className="text-muted">
                                          <i className="fas fa-image me-1"></i>
                                          Single
                                        </small>
                                      )}
                                      {imageCount === 0 && (
                                        <small className="text-muted">
                                          No images
                                        </small>
                                      )}
                                    </div>
                                  </td>
                                  <td className="align-middle">
                                    <div className="btn-group" role="group">
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() =>
                                          navigate(`/dashboard/edit/${product.id}`)
                                        }
                                      >
                                        Edit / Images
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(product.id)}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Pagination Component */}
                      <Pagination />
                    </>
                  )}
                </div>
              )}

              {/* Settings Tab Content */}
              {activeTab === 'settings' && (
                <form onSubmit={handleSaveSettings}>
                  <h3 className="mb-4">Store Settings</h3>

                  <div className="row mb-4">
                    <div className="col-md-12">
                      <div className="mb-3">
                        <label htmlFor="name" className="form-label">
                          Store Name*
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="name"
                          name="name"
                          value={storeForm.name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="description" className="form-label">
                          Description
                        </label>
                        <textarea
                          className="form-control"
                          id="description"
                          name="description"
                          rows="4"
                          value={storeForm.description}
                          onChange={handleInputChange}
                          placeholder="Tell customers about your business..."
                        ></textarea>
                      </div>

                      <div className="mb-3">
                        <label htmlFor="business_type" className="form-label">
                          Business Type
                        </label>
                        <select
                          className="form-select"
                          id="business_type"
                          name="business_type"
                          value={storeForm.business_type}
                          onChange={handleInputChange}
                        >
                          <option value="">Select a business type</option>
                          <option value="Retail Store">Retail Store</option>
                          <option value="Restaurant">Restaurant</option>
                          <option value="Service Business">Service Business</option>
                          <option value="Digital Storefront">Digital Storefront</option>
                        </select>
                      </div>

                      <div className="mb-3">
                        <label htmlFor="city" className="form-label">
                          City*
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="city"
                          name="city"
                          value={storeForm.city}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <hr className="my-4" />

                  <h5 className="mb-3">Location &amp; Contact Information</h5>
                  <div className="row mb-4">
                    <div className="col-md-12 mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="has_physical_location"
                          name="has_physical_location"
                          checked={storeForm.has_physical_location}
                          onChange={handleCheckboxChange}
                        />
                        <label className="form-check-label" htmlFor="has_physical_location">
                          This business has a physical location
                        </label>
                      </div>
                    </div>

                    {storeForm.has_physical_location && (
                      <div className="col-md-12 mb-3">
                        <label htmlFor="address" className="form-label">Address</label>
                        <input
                          type="text"
                          className="form-control"
                          id="address"
                          name="address"
                          value={storeForm.address}
                          onChange={handleInputChange}
                          placeholder="123 Main St, Kelowna, BC"
                        />
                      </div>
                    )}

                    <div className="col-md-6 mb-3">
                      <label htmlFor="contact_email" className="form-label">
                        Contact Email
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="contact_email"
                        name="contact_email"
                        value={storeForm.contact_email}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="phone_number" className="form-label">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        id="phone_number"
                        name="phone_number"
                        value={storeForm.phone_number}
                        onChange={handleInputChange}
                      />
                    </div>

                    <div className="col-md-12 mb-3">
                      <label htmlFor="website" className="form-label">
                        Website
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        id="website"
                        name="website"
                        value={storeForm.website}
                        onChange={handleInputChange}
                        placeholder="https://example.com"
                      />
                    </div>
                  </div>

                  <hr className="my-4" />

                  <h5 className="mb-3">Social Media</h5>
                  <div className="row mb-4">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="instagram" className="form-label">Instagram</label>
                      <div className="input-group">
                        <span className="input-group-text">@</span>
                        <input
                          type="text"
                          className="form-control"
                          id="instagram"
                          value={storeForm.social_media?.instagram || ''}
                          onChange={(e) => handleSocialMediaChange('instagram', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="facebook" className="form-label">Facebook</label>
                      <input
                        type="text"
                        className="form-control"
                        id="facebook"
                        value={storeForm.social_media?.facebook || ''}
                        onChange={(e) => handleSocialMediaChange('facebook', e.target.value)}
                        placeholder="facebook.com/yourbusiness"
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="twitter" className="form-label">Twitter</label>
                      <div className="input-group">
                        <span className="input-group-text">@</span>
                        <input
                          type="text"
                          className="form-control"
                          id="twitter"
                          value={storeForm.social_media?.twitter || ''}
                          onChange={(e) => handleSocialMediaChange('twitter', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label htmlFor="whatsapp" className="form-label">WhatsApp</label>
                      <input
                        type="text"
                        className="form-control"
                        id="whatsapp"
                        value={storeForm.social_media?.whatsapp || ''}
                        onChange={(e) => handleSocialMediaChange('whatsapp', e.target.value)}
                        placeholder="+1 (250) 555-1234"
                      />
                    </div>
                  </div>

                  <hr className="my-4" />

                  <h5 className="mb-3">Business Hours</h5>
                  <div className="card mb-4">
                    <div className="card-body">
                      {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(day => {
                        const dayHours = storeForm.business_hours?.[day] || {
                          open: '09:00',
                          close: '17:00',
                          isClosed: day === 'sunday'
                        };

                        return (
                          <div key={day} className="mb-3 pb-3 border-bottom">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="text-capitalize fw-bold">{day}</span>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`${day}-open`}
                                  checked={!dayHours.isClosed}
                                  onChange={() =>
                                    handleBusinessHoursChange(day, 'isClosed', !dayHours.isClosed)
                                  }
                                />
                                <label className="form-check-label" htmlFor={`${day}-open`}>
                                  {dayHours.isClosed ? 'Closed' : 'Open'}
                                </label>
                              </div>
                            </div>

                            {!dayHours.isClosed && (
                              <div className="row g-3">
                                <div className="col-6">
                                  <label htmlFor={`${day}-open-time`} className="form-label">
                                    Open
                                  </label>
                                  <input
                                    type="time"
                                    className="form-control"
                                    id={`${day}-open-time`}
                                    value={dayHours.open}
                                    onChange={(e) =>
                                      handleBusinessHoursChange(day, 'open', e.target.value)
                                    }
                                  />
                                </div>
                                <div className="col-6">
                                  <label htmlFor={`${day}-close-time`} className="form-label">
                                    Close
                                  </label>
                                  <input
                                    type="time"
                                    className="form-control"
                                    id={`${day}-close-time`}
                                    value={dayHours.close}
                                    onChange={(e) =>
                                      handleBusinessHoursChange(day, 'close', e.target.value)
                                    }
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-secondary me-2"
                      onClick={() => setActiveTab('products')}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={savingSettings}>
                      {savingSettings ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardPage;
